<template>
    <wrapper-features-templates-one-column
    :hideWhiteBoxOnLoad="(showLoader && !showFilters) || (!tableData.length && !showLoader && !applyErrorState && !showFilters) || applyErrorState"
    :showFiltersAndEmptyState="!tableData.length && !showLoader && !applyErrorState && showFilters"
    :showLoader="showLoader"
    :applyErrorState="applyErrorState"
    :errorStateMessage="t('userArea.definitions.history.emptyStates.error')"
    :showFloatingButton="showFilters && !applyErrorState"
    :floatingButtonParams="{ icon: 'filter', label: t('filters.filter')}"
    @floatingButtonClicked="showFiltersModal = true"
    @backOrCloseClicked="navigateByName(routeList.USER_AREA_DEFINITIONS_NAME)">
        <!-- Visible Filters -->
        <consult-filters-wrapper
        v-if="showFilters"
        :filterTags="filterTags"
        :applyButtonDisabled="applyButtonDisabled"
        @applyFilters="applyFilters(appliedFilters)"
        @removeFilterTag="removeFilterTag"
        @closeAllFilters="closeAllFilters">
            <!-- Double Calendar -->
			<input-double-calendar
			calendarDirection="row"
			:fromDateParams="beginDateParams"
			:toDateParams="endDateParams"
			@inputFromDate="appliedFilters.beginDate = $event"
			@inputToDate="appliedFilters.endDate = $event"/>
        </consult-filters-wrapper>
        <!-- Table Generic -->
        <transition name="common-enter-animation" mode="out-in" appear>
            <div v-if="!!tableData.length && !showLoader && !applyErrorState" class="transition-helper-element">
                <table-generic
                :headerData="tableHeaderData"
                :tableStyles="tableStyles"
                :tableData="tableData"
                :hasMore="getHistoryListHasMore"
                :isLoadingMore="tableLoadingMore"
                @loadMore="loadMoreHandler"
                @rowClicked="rowClickedHandler"/>
            </div>
        </transition>
        <!-- Empty State -->
        <template #empty-state-slot>
            <empty-state-generic
            v-if="!tableData.length && !showLoader && !applyErrorState"
            :illustration="emptyStateIllustration"
            :title="emptyStateTitle"
            :description="emptyStateDescription"/>
        </template>
    </wrapper-features-templates-one-column>
    <!-- Filters Modal -->
    <filters-modal
    v-if="showFiltersModal"
    :appliedFilters="appliedFilters"
    :statusList="getStatusList"
    :operationTypesList="operationTypes"
    :channelsList="getChannelsList"
	:beginDateParams="beginDateParams"
	:endDateParams="endDateParams"
    @hideMe="showFiltersModal = false"
    @applyFilters="applyFilters" />
    <!-- Detail Modal -->
    <modal-side-detail-mdw-line-group
    v-if="showDetailsModal"
    :headerColor="headerColor"
    :title="selectedHistory.title"
    :icon="selectedHistory.status.icon"
    :requestPayload="{ operationId: selectedHistory.id }"
    :errorStateMessage="t('emptyStates.genericFeatureErrorMessage')"
    :detailRequestAction="selectedHistory.requestAction"
    :statusIconSvgName="selectedHistory.status.icon"
    :statusIconColor="selectedHistory.status.color"
    @hideMe="showDetailsModal = false"/>
    <!-- Feedback Toasts -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import ConsultFiltersWrapper from '@/components/wrappers/ConsultFiltersWrapper'
import InputDoubleCalendar from '@/components/generic/inputs/DoubleCalendar'
import TableGeneric from '@/components/generic/table/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import FiltersModal from '@/components/features/userArea/definitions/history/FiltersModal'
import ModalSideDetailMdwLineGroup from '@/components/generic/modals/side/DetailMdwLineGroup'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue */
import { ref, computed, onBeforeUnmount } from 'vue'

/* Assets & Helpers */
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { getDate, createJSONDateStartDay, createJSONDateEndDay, getDateWithTime } from '@/assets/scripts/dateBuilders'
import { getCurrentRouteMenuAdditionalData } from '@/assets/scripts/parsers/menu'
import { operationsIconHandler, operationsClassesHandler } from '@/assets/scripts/mappers/operationsHistory'
import { clearObject } from '@/assets/scripts/objects'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const {
    requestChannelsList,
    requestStatusList,
    requestHistoryList,
    clearFeatureStates
} = mapActions(moduleMapper.USER_AREA_DEFINITIONS_HISTORY)

const {
    getHistoryList,
    getHistoryListHasMore,
    getStatusList,
    getChannelsList
} = mapGetters(moduleMapper.USER_AREA_DEFINITIONS_HISTORY)

/* Generic Data */
const showLoader = ref(false)
const applyErrorState = ref(false)
const selectedHistory = ref(null)
const tableLoadingMore = ref(false)
const paginationParams = ref({})

const resetPaginationParams = () => {
    paginationParams.value = {
        pageNumber: 1
    }
}

/*************************************************
*                                                *
*                  Empty States                  *
*                                                *
*************************************************/
const emptyStateIllustration = computed(() => {
    if (filterTags.value.length !== 0) return 'emptyState/filters'

    return 'emptyState/no-movements'
})

const emptyStateTitle = computed(() => {
    if (filterTags.value.length !== 0) return t('userArea.definitions.history.emptyStates.noResults.noFilterTitle')

    return t('userArea.definitions.history.emptyStates.noResults.title')
})

const emptyStateDescription = computed(() => {
    if (filterTags.value.length !== 0) return t('userArea.definitions.history.emptyStates.noResults.noFilterDescription')

    return t('userArea.definitions.history.emptyStates.noResults.description')
})

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const operationTypes = ref(getCurrentRouteMenuAdditionalData().listOperationIds)

const initialRequests = async () => {
    resetPaginationParams()

    showFilters.value = false
    showLoader.value = true
    applyErrorState.value = false
    showFeedbackToast.value = false

    try {
        await requestStatusList()
        await requestChannelsList()
        callHistoryList()
    } catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
		errorHandler(error, initialRequests)
    }
}

const callHistoryList = async () => {
    cancelAllPendingRequests()

    paginationParams.value.pageNumber === 1 ? showLoader.value = true : tableLoadingMore.value = true
    applyErrorState.value = false
    showFeedbackToast.value = false

    const payload = {
        ...paginationParams.value,
        beginDate: createJSONDateStartDay(appliedFilters.value.beginDate),
        endDate: createJSONDateEndDay(appliedFilters.value.endDate),
        status: appliedFilters.value.statusId,
        type: appliedFilters.value.operationType ?? operationTypes.value.map(type => type.id),
        channelId: appliedFilters.value.channel
    }

    try {
        await requestHistoryList(payload)

        if (getHistoryList.value.length > 0) showFilters.value = true
        showLoader.value = false
        tableLoadingMore.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        if (paginationParams.value.pageNumber === 1) applyErrorState.value = true
		errorHandler(error.response, callHistoryList)
    }
}

/*************************************************
*                                                *
*                 Table Data                     *
*                                                *
*************************************************/
const showDetailsModal = ref(false)

const tableHeaderData = [
    {
        label: t('userArea.definitions.history.tableHeaders.type'),
        rowCellType: 'title',
        dataAttr: 'description',
        showInMobile: { groupWith: ['date'] }
    },
    { label: t('userArea.definitions.history.tableHeaders.date'), dataAttr: 'date' },
    { label: t('userArea.definitions.history.tableHeaders.status'), rowCellType: 'status', dataAttr: 'statusDescription', showInMobile: true }
]

const tableStyles = [
    null,
    null,
    null
]

const tableData = computed(() => {
    return getHistoryList.value.map(history => {
        return [
            { value: history.id, dataAttr: 'id' },
            { type: 'title', value: history.description, dataAttr: 'description' },
            {
                type: 'description',
                value: {
                    content: getDateWithTime(history.endDate),
                    classes: 'capitalize'
                },
                dataAttr: 'date'
            },
            {
                type: 'status',
                value: {
                    text: history.state.description,
                    icon: operationsIconHandler(history.state.id),
                    classes: operationsClassesHandler(history.state.id)
                },
                dataAttr: 'status'
            }
        ]
    })
})

const rowClickedHandler = row => {
    setDetailData(row)

    showDetailsModal.value = true
}

const loadMoreHandler = () => {
    paginationParams.value.pageNumber++
    callHistoryList()
}

const setDetailData = row => {
    const selectedOperation = getHistoryList.value.find(history => history.id === row.rowId)

    selectedHistory.value = {
        id: row.rowId,
        operationId: selectedOperation.operation.id,
        title: row.rowData.find(data => data.type === 'title').value,
        status: {
            ...row.rowData.find(data => data.type === 'status').value,
            color: operationsClassesHandler(selectedOperation.state.id)
        },
        requestAction: `${moduleMapper.USER_AREA_DEFINITIONS_HISTORY}/requestOperationDetails`
    }
}

/*************************************************
*                                                *
*                 Details Modal                  *
*                                                *
*************************************************/
const headerColor = computed(() => {
    if (!selectedHistory.value) return

    return `var(--${selectedHistory.value.status.color})`
})

/*************************************************
*                                                *
*                  Filters Logic                 *
*                                                *
*************************************************/
const showFilters = ref(false)
const showFiltersModal = ref(false)

const appliedFilters = ref({
    beginDate: null,
    endDate: null,
    operationType: null,
    statusId: null,
    channel: null
})

const filterTags = ref([])

/* Begin Date Params */
const beginDateParams = computed(() => {
    return {
        labelText: t('userArea.definitions.history.filters.beginDate'),
        placeholder: t('userArea.definitions.history.placeholders.beginDate'),
        inputValue: appliedFilters.value.beginDate,
        disabledDates: { from: new Date() }
    }
})

/* End Date Params */
const endDateParams = computed(() => {
    return {
        labelText: t('userArea.definitions.history.filters.endDate'),
        placeholder: t('userArea.definitions.history.placeholders.endDate'),
        inputValue: appliedFilters.value.endDate,
        disabledDates: { from: new Date() }
    }
})

const applyButtonDisabled = computed(() => {
    return !appliedFilters.value.beginDate && !appliedFilters.value.endDate
})

const applyFilters = (filters) => {
    appliedFilters.value = filters
    filterTags.value = []

    for (const filter in appliedFilters.value) {
		if (appliedFilters.value[filter]) {
            filterTags.value.push({
                id: filter,
                label: t(`userArea.definitions.history.filters.${filter}`),
                text: filterTagTextHandler(filter, appliedFilters.value[filter])
            })
        } else {
			appliedFilters.value[filter] = null
		}
	}

    resetPaginationParams()
    callHistoryList()
}

const removeFilterTag = (tagId) => {
    appliedFilters.value[tagId] = null
	filterTags.value = filterTags.value.filter(filter => filter.id !== tagId)

    if (!filterTags.value.length) filterTags.value = []
    resetPaginationParams()
    callHistoryList()
}

const closeAllFilters = () => {
    filterTags.value = []
    appliedFilters.value = clearObject(appliedFilters.value)
    resetPaginationParams()
    callHistoryList()
}

const filterTagTextHandler = (filter, value) => {
	switch (filter) {
		case 'beginDate':
		case 'endDate':
			return getDate(value)
        case 'operationType':
			return operationTypes.value.find(type => type.id === value).name
        case 'statusId':
            return getStatusList.value.find(status => status.id === value).description
        case 'channel':
            return getChannelsList.value.find(channel => channel.id === value).description
		default: return value
	}
}

/*************************************************
*                                                *
*                 Error Feedback                 *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    showLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

initialRequests()

onBeforeUnmount(() => {
    clearFeatureStates()
})
</script>

<style lang="scss" scoped>
.transition-helper-element {
    @include commonEnteringAnimation;
}
</style>
